// Story: Map to Solution inherits Account context
// (docs/specs/manage-staffing-request-template.md §5).
//
// A matched Account contributes truth the submitter must not re-enter: its CURRENT
// Expectation Level (read-only — Expectation is an Account-level attribute, never a
// per-request value) and its constraint profile as the defaults (§7 decision:
// default from the Account when matched; submitter-entered only for net-new).
// Template Section B picks render alongside as "suggested by upload" so the RM sees
// Account truth vs inbound suggestion side by side — requests never drift from
// Account truth (BC). Everything here is display context; nothing writes back.

import { Building2 } from "lucide-react";
import { Tag } from "@/components/ui/tag";
import type { StaffingRequestDetailResponse } from "@/hooks/use-staffing-requests";
import { expectationLevelDef } from "@/lib/expectation-levels";

const GROUP_ORDER = ["Offering", "Platform", "Domain", "ICP"];

function groupSort(a: { groupName: string }, b: { groupName: string }): number {
  return GROUP_ORDER.indexOf(a.groupName) - GROUP_ORDER.indexOf(b.groupName);
}

interface Props {
  detail: StaffingRequestDetailResponse;
}

export function AccountContextPanel({ detail }: Props) {
  const { request, account, accountConstraints, constraintSuggestions } = detail;
  const isTemplate = request.sourceType === "Template";

  // Nothing to show for a non-template request with no matched Account.
  if (!isTemplate && !account) return null;

  const expectation = account ? expectationLevelDef(account.expectationLevel) : undefined;

  return (
    <div className="mb-4 rounded-md border border-border bg-card px-4 py-3">
      <div className="mb-2 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <p className="text-[12px] font-semibold">Account context</p>
      </div>

      <div className="space-y-2 text-[12px]">
        {/* Expectation Level — inherited from the Account, read-only (AC). */}
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="text-muted-foreground">Expectation Level:</span>
          {account ? (
            <span
              className="font-medium"
              title="Inherited from the Account — set in Account Management, not on the request."
            >
              {account.expectationLevel}
              {expectation ? ` — ${expectation.label}` : ""}
              <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                inherited from Account · read-only
              </span>
            </span>
          ) : (
            <span className="italic text-muted-foreground">
              Expectation set on Account after creation.
            </span>
          )}
        </div>

        {/* Account constraint profile — the defaults for a matched Account (§7). */}
        {account && accountConstraints.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground">From Account (default):</span>
            {[...accountConstraints].sort(groupSort).map((c) => (
              <Tag
                key={c.id}
                variant="stroke"
                className="h-auto gap-1 rounded-full bg-muted/40 px-2 py-0.5 text-[11px] font-normal"
                title={`${c.groupName} constraint on the Account — the default for this request.`}
              >
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {c.groupName}
                </span>
                {c.name}
              </Tag>
            ))}
          </div>
        )}

        {/* Template Section B picks — inbound suggestions the RM confirms. */}
        {constraintSuggestions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground">Suggested by upload:</span>
            {[...constraintSuggestions].sort(groupSort).map((s) => (
              <Tag
                key={s.id}
                variant="stroke"
                className={[
                  "h-auto gap-1 rounded-full px-2 py-0.5 text-[11px] font-normal",
                  s.constraintId
                    ? "border-warning-200 bg-warning-50 text-warning-800 dark:border-warning-900/40 dark:bg-warning-950/30 dark:text-warning-300"
                    : "border-error-200 bg-error-50 text-error-700 dark:border-error-900/40 dark:bg-error-950/30 dark:text-error-300",
                ].join(" ")}
                title={
                  s.constraintId
                    ? "Suggested by the uploaded template — the RM confirms before it influences anything."
                    : "Not in the current Configuration — pick a live value when confirming."
                }
              >
                <span className="text-[10px] uppercase tracking-wide opacity-70">
                  {s.groupName}
                </span>
                {s.value}
                {!s.constraintId && <span className="text-[10px]">· not in Configuration</span>}
              </Tag>
            ))}
          </div>
        )}

        {/* Section A names — what the submitter called the engagement (net-new aid). */}
        {isTemplate && !account && (request.sourceAccountName || request.sourceSolutionName) && (
          <div className="text-[11px] text-muted-foreground">
            Template names:{" "}
            {request.sourceAccountName && <span>Account “{request.sourceAccountName}”</span>}
            {request.sourceAccountName && request.sourceSolutionName && " · "}
            {request.sourceSolutionName && <span>Solution “{request.sourceSolutionName}”</span>}
          </div>
        )}
      </div>
    </div>
  );
}
